import React, { useState } from 'react';
import { ClockIcon, ChevronDownIcon } from '../chat/SidebarIcons';
import { formatEstimateTime } from '../../utils/estimateTime';
import { getLastOccurrenceHistory, formatOccurrenceParts, getReminderTriggerDisplay, reminderSkippedOnDate, reminderTaskProgress, formatReminderScheduleSummary, getTodayDateStringInTimezone, shiftDateString } from './reminderUtils';
import SnoozeDropdown from './SnoozeDropdown';
import ReminderTaskChecklist from './ReminderTaskChecklist';

const CheckIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const chipBase =
  'inline-flex items-center gap-0.5 max-w-[9rem] min-h-[1.375rem] px-1.5 py-0.5 rounded-md text-[10px] font-semibold leading-tight truncate border';

const STATUS_CHIP = {
  PENDING: 'border-amber-200/90 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/25 dark:text-amber-100',
  ACCEPTED: 'border-emerald-200/90 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-900/25 dark:text-emerald-200',
  REJECTED: 'border-rose-200/90 bg-rose-50 text-rose-800 dark:border-rose-700/60 dark:bg-rose-900/25 dark:text-rose-200',
  REVOKED: 'border-gray-200/90 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-300',
};

const HISTORY_TONE = {
  done: 'border-green-500 bg-green-500 text-white dark:border-green-500 dark:bg-green-600 dark:text-white',
  pending: 'border-yellow-400 bg-yellow-400 text-yellow-950 dark:border-yellow-400 dark:bg-yellow-500 dark:text-yellow-950',
  missed: 'border-red-500 bg-red-500 text-white dark:border-red-500 dark:bg-red-600 dark:text-white',
};

const occurrenceTone = (item) => {
  const progress = item.taskProgress;
  if (progress && progress.total > 0) {
    const detail = `${progress.done} of ${progress.total} tasks`;
    if (progress.percent >= 100) {
      return { label: '100%', tone: 'done', detail };
    }
    if (progress.done > 0) {
      return { label: `${progress.percent}%`, tone: 'pending', detail };
    }
    if (item.skipped) return { label: 'Missed', tone: 'missed', detail };
    if (item.isToday) return { label: 'Pending', tone: 'pending', detail };
    return { label: '0%', tone: 'missed', detail };
  }
  if (item.completed) return { label: 'Done', tone: 'done', detail: 'Finished' };
  if (item.skipped) return { label: 'Missed', tone: 'missed', detail: 'Skipped' };
  if (item.isToday) return { label: 'Pending', tone: 'pending', detail: 'Not finished yet' };
  return { label: 'Missed', tone: 'missed', detail: 'Not finished' };
};

export const ReminderHistoryRow = ({ reminder, showRangePicker = false }) => {
  const todayStr = getTodayDateStringInTimezone(reminder?.timezone);
  const [range, setRange] = useState('5');
  const [customFrom, setCustomFrom] = useState(() => shiftDateString(todayStr, -29));
  const [customTo, setCustomTo] = useState(todayStr);

  const history = (() => {
    if (!showRangePicker) {
      return getLastOccurrenceHistory(reminder, 5);
    }
    if (range === '5') {
      return getLastOccurrenceHistory(reminder, {
        fromDate: shiftDateString(todayStr, -4),
        toDate: todayStr,
      });
    }
    if (range === '30') {
      return getLastOccurrenceHistory(reminder, {
        fromDate: shiftDateString(todayStr, -29),
        toDate: todayStr,
      });
    }
    const fromDate = customFrom && customTo && customFrom > customTo ? customTo : customFrom;
    const toDate = customFrom && customTo && customFrom > customTo ? customFrom : customTo;
    return getLastOccurrenceHistory(reminder, { fromDate, toDate });
  })();

  const heading = !showRangePicker
    ? 'Last 5 times status'
    : range === '5'
      ? 'Last 5 days'
      : range === '30'
        ? 'Last 30 days'
        : 'Custom dates';

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {heading}
        </p>
        {showRangePicker ? (
          <div className="flex flex-wrap items-center gap-1">
            {[
              { id: '5', label: 'Last 5 days' },
              { id: '30', label: 'Last 30 days' },
              { id: 'custom', label: 'Custom' },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setRange(option.id)}
                className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${
                  range === option.id
                    ? 'bg-amber-500 text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {showRangePicker && range === 'custom' ? (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <label className="text-[11px] text-gray-500 dark:text-gray-400">
            From
            <input
              type="date"
              max={todayStr}
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="ml-1 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-0.5 text-[11px] text-gray-800 dark:text-gray-100"
            />
          </label>
          <label className="text-[11px] text-gray-500 dark:text-gray-400">
            To
            <input
              type="date"
              max={todayStr}
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="ml-1 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-0.5 text-[11px] text-gray-800 dark:text-gray-100"
            />
          </label>
        </div>
      ) : null}
      {history.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-gray-500">No scheduled days in this range</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {history.map((item) => {
            const status = occurrenceTone(item);
            const parts = formatOccurrenceParts(item.date);
            return (
              <span
                key={item.date}
                title={`${item.date}: ${status.detail}`}
                className={`inline-flex flex-col items-center justify-center min-w-[3.75rem] px-1.5 py-1.5 rounded-md border text-center ${HISTORY_TONE[status.tone]}`}
              >
                <span className="text-[12px] font-bold leading-none tabular-nums">
                  {parts.day}
                </span>
                <span className="text-[13px] font-bold leading-none tracking-wide mt-1">
                  {parts.month}
                </span>
                <span className="text-[10px] font-bold leading-none mt-1">
                  {status.label}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ReminderItem = ({
  reminder,
  currentUserId,
  onEdit,
  onClone,
  onDelete,
  onView,
  onCompleteForToday,
  completingReminderId,
  onSkipForToday,
  skippingReminderId,
  forMeMode,
  onAccept,
  onReject,
  onRevoke,
  actionLoadingId,
  isCompleted = false,
  selectedDay,
  todayStr,
  nowMs,
  compactActions = false,
  onSnooze,
  variant = 'row',
  onToggleTask,
  togglingTaskId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isCard = variant === 'card';
  const triggerDisplay = getReminderTriggerDisplay(reminder, selectedDay || todayStr, todayStr, nowMs);
  const skipped = reminderSkippedOnDate(reminder, selectedDay || todayStr);
  const overdue = !isCompleted && !skipped && triggerDisplay.overdue;
  const canSkip = !forMeMode && Boolean(onSkipForToday);
  const dayKey = selectedDay || todayStr;
  const taskProgress = reminderTaskProgress(reminder, dayKey);

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm('Delete this reminder?')) {
      onDelete(reminder);
    }
  };

  const statusLabel =
    reminder.status === 'PENDING'
      ? 'Pending'
      : reminder.status === 'ACCEPTED'
        ? 'Accepted'
        : reminder.status === 'REJECTED'
          ? 'Rejected'
          : reminder.status === 'REVOKED'
            ? 'Revoked'
            : null;
  const showCreatorBadge = forMeMode && reminder.creatorUsername;
  const showTargetBadge = !forMeMode && reminder.targetUserId && (reminder.targetUsername || reminder.targetUserId);
  const estimateLabel = reminder.estimateTimeInMinutes
    ? formatEstimateTime(reminder.estimateTimeInMinutes)
    : null;
  const hasMeta = isCompleted || skipped || showCreatorBadge || showTargetBadge || (forMeMode && statusLabel) || (Boolean(taskProgress) && !isCard);
  const description = (reminder.description || '').trim();
  const canEdit = !forMeMode && reminder.userId === currentUserId;
  const canComplete = !forMeMode && Boolean(onCompleteForToday);
  const isPendingForMe = forMeMode && String(reminder.status || '').toUpperCase() === 'PENDING';
  const isAcceptedForMe = forMeMode && String(reminder.status || '').toUpperCase() === 'ACCEPTED';
  const canClone = Boolean(onClone) && !isPendingForMe;

  const openPopup = () => {
    if (isPendingForMe) return;
    onView?.(reminder);
  };

  if (isCard) {
    const scheduleLabel = formatReminderScheduleSummary(reminder);
    return (
      <div
        className="group min-w-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-orange-300 dark:hover:border-orange-700 hover:bg-orange-50/40 dark:hover:bg-gray-700/40 cursor-pointer px-2.5 py-2"
        onClick={openPopup}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300">
            <ClockIcon className="w-3.5 h-3.5" />
          </span>
          <p
            className="min-w-0 flex-1 truncate text-[13px] font-semibold text-gray-900 dark:text-white"
            title={reminder.title || '(No title)'}
          >
            {reminder.title || '(No title)'}
          </p>
          {(isPendingForMe || canEdit || canClone) ? (
            <div
              className="flex items-center gap-1.5 flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              {isPendingForMe ? (
                <>
                  <button
                    type="button"
                    onClick={() => onAccept && onAccept(reminder)}
                    disabled={actionLoadingId === reminder.id}
                    className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 disabled:opacity-50"
                  >
                    {actionLoadingId === reminder.id ? '…' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    onClick={() => onReject && onReject(reminder)}
                    disabled={actionLoadingId === reminder.id}
                    className="text-[11px] font-semibold text-rose-600 dark:text-rose-400 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </>
              ) : (
                <>
                  {canClone ? (
                    <button
                      type="button"
                      onClick={() => onClone(reminder)}
                      className="text-[11px] font-semibold text-gray-600 dark:text-gray-300"
                    >
                      Clone
                    </button>
                  ) : null}
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => onEdit(reminder)}
                      className="text-[11px] font-semibold text-primary-600 dark:text-primary-400"
                    >
                      Edit
                    </button>
                  ) : null}
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="text-[11px] font-semibold text-rose-600 dark:text-rose-400"
                    >
                      Delete
                    </button>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
        </div>
        {scheduleLabel ? (
          <p className="mt-0.5 ml-9 text-[11px] font-medium text-gray-500 dark:text-gray-400 truncate" title={scheduleLabel}>
            {scheduleLabel}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`group min-w-0 transition-colors cursor-pointer ${
        isCard
          ? 'h-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-orange-300 dark:hover:border-orange-700 hover:shadow-sm'
          : 'w-full bg-white dark:bg-gray-800 hover:bg-orange-50/50 dark:hover:bg-gray-700/30'
      } ${
        isCompleted ? 'opacity-80' : ''
      } ${overdue ? (isCard ? 'border-red-200 dark:border-red-800 bg-red-50/70 dark:bg-red-950/25' : 'bg-red-50/70 dark:bg-red-950/25 hover:bg-red-50 dark:hover:bg-red-950/40') : ''}`}
      onClick={openPopup}
    >
      <div className={`flex items-start gap-2.5 min-w-0 ${isCard ? 'flex-col p-3.5' : 'px-3.5 py-3'}`}>
        <span
          className={`inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl ${
            isCompleted
              ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/35 dark:text-emerald-300'
              : overdue
                ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300'
                : 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300'
          }`}
        >
          {isCompleted ? (
            <CheckIcon className="w-4 h-4" />
          ) : (
            <ClockIcon className="w-4 h-4" />
          )}
        </span>

        <div className="min-w-0 flex-1 text-left">
          {hasMeta && (
            <div className="flex flex-wrap items-center gap-1 min-w-0 mb-1.5">
              {isCompleted && (
                <span className={`${chipBase} border-emerald-200/90 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-900/25 dark:text-emerald-200`}>
                  Done
                </span>
              )}
              {taskProgress && !isCard && (
                <span className={`${chipBase} border-sky-200/90 bg-sky-50 text-sky-800 dark:border-sky-700/60 dark:bg-sky-900/25 dark:text-sky-200`}>
                  {taskProgress.done}/{taskProgress.total} · {taskProgress.percent}%
                </span>
              )}
              {skipped && !isCompleted && (
                <span className={`${chipBase} border-red-200/90 bg-red-50 text-red-800 dark:border-red-700/60 dark:bg-red-900/25 dark:text-red-200`}>
                  Missed
                </span>
              )}
              {forMeMode && statusLabel && (
                <span className={`${chipBase} ${STATUS_CHIP[reminder.status] || STATUS_CHIP.REVOKED}`}>
                  {statusLabel}
                </span>
              )}
              {showTargetBadge && (
                <span
                  className={`${chipBase} border-sky-200/90 bg-sky-50 text-sky-800 dark:border-sky-700/60 dark:bg-sky-900/25 dark:text-sky-200`}
                  title={`For ${reminder.targetUsername || reminder.targetUserId}`}
                >
                  @{reminder.targetUsername || reminder.targetUserId}
                </span>
              )}
              {showCreatorBadge && (
                <span
                  className={`${chipBase} border-violet-200/90 bg-violet-50 text-violet-800 dark:border-violet-700/60 dark:bg-violet-900/25 dark:text-violet-200`}
                  title={`From ${reminder.creatorUsername}`}
                >
                  From {reminder.creatorUsername}
                </span>
              )}
            </div>
          )}
          <div className="flex items-baseline gap-1.5 min-w-0">
            <p
              className={`text-[15px] font-bold tracking-tight min-w-0 leading-snug ${
                overdue ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-white'
              } ${
                expanded || isCard ? 'whitespace-normal break-words' : 'truncate'
              } ${isCard ? 'line-clamp-2' : ''}`}
              title={reminder.title || '(No title)'}
            >
              {reminder.title || '(No title)'}
            </p>
            {estimateLabel && (
              <span
                className="flex-shrink-0 text-[11px] font-medium text-gray-500 dark:text-gray-400 tabular-nums"
                title="Estimated time"
              >
                · Est {estimateLabel}
              </span>
            )}
          </div>
          {triggerDisplay.clockLabel ? (
            <p
              className={`mt-0.5 text-[12px] font-semibold tabular-nums ${
                overdue
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {skipped ? 'Skipped · Missed' : overdue ? 'Overdue' : triggerDisplay.snoozed ? 'Snoozed until' : 'Triggers at'}
              {' · '}
              {triggerDisplay.clockLabel}
            </p>
          ) : null}
        </div>

        {onSnooze && !isPendingForMe && (
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <SnoozeDropdown
              size="sm"
              onSnooze={(minutes) => onSnooze(minutes, reminder)}
            />
          </div>
        )}
        {isPendingForMe && (
          <div
            className="flex items-center gap-1.5 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => onAccept && onAccept(reminder)}
              disabled={actionLoadingId === reminder.id}
              className="px-2.5 py-1 text-[11px] font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
            >
              {actionLoadingId === reminder.id ? '…' : 'Approve'}
            </button>
            <button
              type="button"
              onClick={() => onReject && onReject(reminder)}
              disabled={actionLoadingId === reminder.id}
              className="px-2.5 py-1 text-[11px] font-semibold rounded-lg text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        )}
        {canSkip && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSkipForToday(reminder);
            }}
            disabled={skippingReminderId === reminder.id}
            className="flex-shrink-0 px-2 py-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
          >
            {skippingReminderId === reminder.id ? 'Skipping…' : 'Skip today'}
          </button>
        )}
        {(compactActions && (canEdit || canClone)) && (
          <div
            className="flex items-center gap-1 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {canClone ? (
              <button
                type="button"
                onClick={() => onClone(reminder)}
                className="px-2 py-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Clone
              </button>
            ) : null}
            {canEdit ? (
              <button
                type="button"
                onClick={() => onEdit(reminder)}
                className="px-2 py-1 text-[11px] font-semibold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg"
              >
                Edit
              </button>
            ) : null}
            {canEdit ? (
              <button
                type="button"
                onClick={handleDelete}
                className="px-2 py-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg"
              >
                Delete
              </button>
            ) : null}
          </div>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-0.5 p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors flex-shrink-0"
          title={expanded ? 'Hide details' : 'Show details'}
          aria-expanded={expanded}
        >
          <ChevronDownIcon
            className={`w-3.5 h-3.5 transition-transform duration-200 ${
              expanded ? '' : '-rotate-90'
            }`}
          />
        </button>
      </div>

      {expanded && (
        <div
          className={`px-3.5 pb-3 space-y-3 ${isCard ? '' : 'pl-[3.75rem]'}`}
          onClick={openPopup}
        >
          {description ? (
            <p className="text-[13px] text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words">
              {description}
            </p>
          ) : (
            <p className="text-[12px] text-gray-400 dark:text-gray-500">No description</p>
          )}

          {onToggleTask && !isCard ? (
            <div onClick={(e) => e.stopPropagation()}>
              <ReminderTaskChecklist
                reminder={reminder}
                dateStr={dayKey}
                onToggleTask={onToggleTask}
                disabled={Boolean(togglingTaskId)}
              />
            </div>
          ) : null}

          <ReminderHistoryRow reminder={reminder} />

          <div
            className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100/80 dark:border-gray-700/50"
            onClick={(e) => e.stopPropagation()}
          >
            {isAcceptedForMe && (
              <button
                type="button"
                onClick={() => onRevoke && onRevoke(reminder)}
                disabled={actionLoadingId === reminder.id}
                className="text-[11px] font-semibold text-gray-600 hover:text-gray-800 dark:text-gray-300 disabled:opacity-50"
              >
                {actionLoadingId === reminder.id ? '…' : 'Revoke'}
              </button>
            )}
            {canClone && (
              <button
                type="button"
                onClick={() => onClone(reminder)}
                className="text-[11px] font-semibold text-gray-600 hover:text-gray-800 dark:text-gray-300"
              >
                Clone
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => onEdit(reminder)}
                className="text-[11px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                Edit
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={handleDelete}
                className="text-[11px] font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400"
              >
                Delete
              </button>
            )}
            {canComplete && (
              <button
                type="button"
                onClick={() => onCompleteForToday(reminder)}
                disabled={completingReminderId === reminder.id}
                className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 disabled:opacity-50"
              >
                {completingReminderId === reminder.id ? 'Completing…' : 'Complete'}
              </button>
            )}
            {canSkip && (
              <button
                type="button"
                onClick={() => onSkipForToday(reminder)}
                disabled={skippingReminderId === reminder.id}
                className="text-[11px] font-semibold text-gray-600 hover:text-gray-800 dark:text-gray-300 disabled:opacity-50"
              >
                {skippingReminderId === reminder.id ? 'Skipping…' : 'Skip today'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReminderItem;
